import React, { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { submitCalculatorRequest, testSupabaseConnection } from '@/services/supabaseService';
import FooterInfo from './footer/FooterInfo';
import FooterLinks from './footer/FooterLinks';
import SubmissionForm from './footer/SubmissionForm';
import { Separator } from './ui/separator';

/**
 * Site footer — four-column layout:
 * 1. Branding / tagline  2. Calculator links  3. Company links  4. Feedback form
 * Supabase connection is tested only on form submission.
 */
const Footer: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (data: { email: string; calculatorDescription: string }) => {
    try {
      setIsSubmitting(true);

      const connectionTest = await testSupabaseConnection();
      if (!connectionTest.success) {
        toast({
          title: "Connection Error",
          description: "Unable to connect to the database. Please try again later.",
          variant: "destructive",
        });
        return;
      }

      await submitCalculatorRequest(data.email, data.calculatorDescription);
      toast({
        title: "Request Submitted Successfully",
        description: "Thank you for your calculator suggestion! We'll review it soon.",
      });
    } catch (error) {
      let errorMessage = "Failed to submit request. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes("permission denied")) {
          errorMessage = "Permission denied for this operation. This might be a database configuration issue.";
        } else if (error.message.includes("network")) {
          errorMessage = "Network error. Please check your internet connection.";
        } else {
          console.error('Submission error details:', error.message);
          errorMessage = "Unable to submit your request. Please try again later.";
        }
      }
      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-muted/50 mt-20">
      <div className="container mx-auto px-6 md:px-10 py-12 md:py-16">
        {/* Main grid — responsive: stacks on mobile, 4 cols on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Col 1: Branding */}
          <FooterInfo />

          {/* Cols 2–3: Quick links */}
          <FooterLinks />

          {/* Col 4: Feedback form */}
          <div>
            <h4 className="font-semibold text-foreground text-sm mb-3">Send Feedback</h4>
            <SubmissionForm
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              isDisabled={false}
            />
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <Separator />
      <div className="container mx-auto px-6 md:px-10 py-4">
        <p className="text-muted-foreground/60 text-xs text-center">
          Built with care for people who need quick, reliable answers.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
